import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PawPrint, Plus, Truck } from "lucide-react";
import ReviewForm from "@/components/ReviewForm";

const STATUS_COLORS = {
  pending_payment: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  assigned: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  picked_up: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  in_transit: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  delivered: "bg-primary/20 text-primary border-primary/30",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return nav("/login", { state: { next: "/dashboard" } });
    if (!user) return;
    api.get("/bookings/me").then(({ data }) => setBookings(data)).finally(() => setLoading(false));
  }, [user, nav]);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Hello {user.name}</span>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Your bookings</h1>
        </div>
        <Button asChild data-testid="dashboard-new-booking-btn">
          <Link to="/book"><Plus size={16} className="mr-2" /> New booking</Link>
        </Button>
      </div>

      <div className="mt-10">
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : bookings.length === 0 ? (
          <Card className="p-12 text-center border-border">
            <PawPrint className="mx-auto text-muted-foreground" size={32} />
            <h2 className="font-display text-2xl mt-4">No bookings yet</h2>
            <p className="text-muted-foreground mt-2">Get started with your first transport.</p>
            <Button asChild className="mt-6"><Link to="/book">Book a journey</Link></Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {bookings.map((b) => (
              <Card key={b.id} className="p-6 border-border" data-testid={`dashboard-booking-${b.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{b.pickup_date}</p>
                    <h3 className="font-display text-lg mt-1">{b.pickup_location} → {b.dropoff_location}</h3>
                  </div>
                  <Badge className={STATUS_COLORS[b.status] || "bg-secondary"}>{String(b.status).replace("_", " ")}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-5 text-sm">
                  <div><p className="text-xs text-muted-foreground">Distance</p><p>{b.distance_miles} mi</p></div>
                  <div><p className="text-xs text-muted-foreground">Vehicle</p><p className="capitalize">{b.vehicle}</p></div>
                  <div><p className="text-xs text-muted-foreground">Pet</p><p>{b.pet_name}</p></div>
                </div>
                <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
                  <p className="font-display text-2xl">£{b.total_price}</p>
                  <span className="text-xs text-muted-foreground capitalize">{b.payment_status}</span>
                </div>
                {b.status === "delivered" && (
                  <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground">Rate your journey</p>
                    <ReviewForm bookingId={b.id} />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
